import React, { useCallback, useMemo } from "react";

import {
  AppBar,
  Authenticator,
  CircularProgressCenter,
  Drawer,
  FireCMS,
  ModeControllerProvider,
  NavigationRoutes,
  Scaffold,
  SideDialogs,
  SnackbarProvider,
  useBuildLocalConfigurationPersistence,
  useBuildModeController,
  useBuildNavigationController,
  useValidateAuthenticator,
} from "@firecms/core";
import {
  FirebaseAuthController,
  FirebaseLoginView,
  FirebaseSignInProvider,
  FirebaseUserWrapper,
  localSearchControllerBuilder,
  useFirebaseAuthController,
  useFirebaseStorageSource,
  useFirestoreDelegate,
  useInitialiseFirebase,
} from "@firecms/firebase";
import { CenteredView } from "@firecms/ui";

import { firebaseConfig } from "./firebase_config";
import { appConfigCollection } from "./collections/app_config";
import { backgroundStyleCollection } from "./collections/background_style";
import { cardStyleCollection } from "./collections/card_style";
import { headerStyleCollection } from "./collections/header_style";
import { titleStyleCollection } from "./collections/title_style";
import { templateCollection } from "./collections/template";
import { gameOptionsCollection } from "./collections/game_options";
import { winConditionCollection } from "./collections/win_condition";
import { winConditionGroupsCollection } from "./collections/win_condition_groups";
import {
  doc,
  getDoc,
  getFirestore,
} from "firebase/firestore";
import { initializeApp } from "firebase/app";
import logo from "./assets/logo.png";

function App() {
  // Use your own authentication logic here
  const myAuthenticator: Authenticator<FirebaseUserWrapper> = useCallback(
    async ({ user, authController }) => {
      //   if (user?.email?.includes("josh")) {
      //     // You can throw an error to prevent access
      //     throw Error("Silly Josh!");
      //   }
      if (user?.email?.includes("flanders")) {
        // You can throw an error to prevent access
        throw Error("Stupid Flanders!");
      }
      //   console.log(authController.user);
      const idTokenResult = await user?.firebaseUser?.getIdTokenResult();
      //   throw Error(`${idTokenResult?.claims.user_id ?? ""}`);
      //   const userIsAdmin =
      // idTokenResult?.claims.admin || user?.email?.endsWith("@firecms.co");
      const firebaseApp = await initializeApp(firebaseConfig);
      const firestore = await getFirestore(firebaseApp);
      const user_id = idTokenResult?.claims.user_id;
      const userDocRef = doc(firestore, `cms_user/${user_id}`);
      const userDocSnapshot = await getDoc(userDocRef);
      if (
        !userDocSnapshot.exists() ||
        userDocSnapshot.data() === undefined ||
        userDocSnapshot.data().admin != true
      ) {
        throw Error(
          "You do not have the necessary admin permissions to access this application. Please contact support if you believe this is an error."
        );
      }

      const userData = userDocSnapshot.data();

      return true;
    },
    []
  );

  //backgroundStyleCollection, cardStyleCollection, headerStyleCollection, titleStyleCollection, gameOptionsCollection, winConditionCollection, winConditionGroupsCollection, templateCollection
  const collections = useMemo(
    () => [
      appConfigCollection,
      backgroundStyleCollection,
      cardStyleCollection,
      headerStyleCollection,
      titleStyleCollection,
      templateCollection,
      gameOptionsCollection,
      winConditionCollection,
      winConditionGroupsCollection,
    ],
    []
  );

  const { firebaseApp, firebaseConfigLoading, configError } =
    useInitialiseFirebase({
      firebaseConfig,
    });

  // Controller used to manage the dark or light color mode
  const modeController = useBuildModeController();

  const signInOptions: FirebaseSignInProvider[] = ["google.com", "password"];

  // Controller for managing authentication
  const authController: FirebaseAuthController = useFirebaseAuthController({
    firebaseApp,
    signInOptions,
  });

  // Controller for saving some user preferences locally.
  const userConfigPersistence = useBuildLocalConfigurationPersistence();

  // Delegate used for fetching and saving data in Firestore
  const firestoreDelegate = useFirestoreDelegate({
    firebaseApp,
    textSearchControllerBuilder: localSearchControllerBuilder,
  });

  // Controller used for saving and fetching files in storage
  const storageSource = useFirebaseStorageSource({
    firebaseApp,
  });

  const { authLoading, canAccessMainView, notAllowedError } =
    useValidateAuthenticator({
      authController,
      authenticator: myAuthenticator,
      dataSourceDelegate: firestoreDelegate,
      storageSource,
    });

  const navigationController = useBuildNavigationController({
    disabled: authLoading,
    collections,
    authController,
    dataSourceDelegate: firestoreDelegate,
  });

  if (firebaseConfigLoading || !firebaseApp) {
    return (
      <>
        <CircularProgressCenter />
      </>
    );
  }

  if (configError) {
    return <CenteredView>{configError}</CenteredView>;
  }

  return (
    <SnackbarProvider>
      <ModeControllerProvider value={modeController}>
        <FireCMS
          navigationController={navigationController}
          authController={authController}
          userConfigPersistence={userConfigPersistence}
          dataSourceDelegate={firestoreDelegate}
          storageSource={storageSource}
        >
          {({ context, loading }) => {
            if (loading || authLoading) {
              return <CircularProgressCenter size={"large"} />;
            }

            if (!canAccessMainView) {
              return (
                <FirebaseLoginView
                  authController={authController}
                  firebaseApp={firebaseApp}
                  signInOptions={signInOptions}
                  notAllowedError={notAllowedError}
                />
              );
            }

            return (
              <Scaffold autoOpenDrawer={false} logo={logo}>
                <AppBar
                  title={"My Bingo Card"}
                  includeModeToggle={true}
                  logo={logo}
                />
                <Drawer />
                <NavigationRoutes />
                <SideDialogs />
              </Scaffold>
            );
          }}
        </FireCMS>
      </ModeControllerProvider>
    </SnackbarProvider>
  );
}

export default App;
